import { ReactNode, useMemo } from 'react';
import {
  ImageBackgroundProps,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { mvs } from 'react-native-size-matters';

import { BicolorBackground } from './BicolorBackground';
import { GradientBackground } from './GradientBackground';
import { ImageBackground } from './ImageBackground';
import { useAppSelector } from '../app/hooks';
import {
  COLORS,
  DEFAULT_BACKGROUND_BLUR_RADIUS,
  IS_WEB,
  REDUCED_HORIZONTAL_MARGIN,
} from '../constants';
import {
  selectBackgroundImage,
  selectBackgroundOption,
  selectDefaultCover,
  selectGradientBackgroundDirection,
  selectHasMargin,
  selectPodcastBackgroundColor,
  selectRadioTab,
  selectShowBlurredBackground,
} from '../features/appconfig/store/selectors';
import { selectCurrentTrack } from '../features/currenttrack/store/selectors';

interface Props {
  headerContents: ReactNode;
  children: ReactNode;
  contentStyle?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  backgroundBlurRadius?: ImageBackgroundProps['blurRadius'];
  displayAdBanner?: boolean;
  isPodcastModule?: boolean; // if true, apply specific optional backgound
}

export const ScreenLayout = ({
  headerContents,
  children,
  contentStyle,
  headerStyle,
  isPodcastModule,
}: Props) => {
  const backgroundOption = useAppSelector(selectBackgroundOption);
  const backgroundImage = useAppSelector(selectBackgroundImage);
  const defaultBackgroundCover = useAppSelector(selectDefaultCover);
  const radioTab = useAppSelector(selectRadioTab);
  const gradientBackgroundDirection = useAppSelector(selectGradientBackgroundDirection);
  const currentTrack = useAppSelector(selectCurrentTrack);
  const showBlurredBackground = useAppSelector(selectShowBlurredBackground);
  const podcastModuleBackground = useAppSelector(selectPodcastBackgroundColor);
  const mustBeColorBackground = isPodcastModule && podcastModuleBackground !== null;

  // applies custom margin config
  const hasMargin = useAppSelector(selectHasMargin);
  const horizontalMargin = hasMargin ? mvs(32) : REDUCED_HORIZONTAL_MARGIN;

  const header = useMemo(() => {
    const headerStyles = [
      styles.header,
      headerStyle,
      { marginHorizontal: horizontalMargin },
    ];
    return (
      <GradientBackground style={IS_WEB ? styles.webOffset : undefined}>
        <SafeAreaView edges={['top']}>
          <View style={headerStyles}>{headerContents}</View>
        </SafeAreaView>
      </GradientBackground>
    );
  }, [headerContents, headerStyle, horizontalMargin]);

  // color background
  if (backgroundOption === 'color' || mustBeColorBackground) {
    // solid background
    if (radioTab?.backgroundType === 'solid' || mustBeColorBackground) {
      return (
        <View style={[styles.content, { backgroundColor: COLORS.WHITE }]}>
          <View
            style={[
              styles.content,
              {
                backgroundColor: mustBeColorBackground
                  ? podcastModuleBackground
                  : radioTab?.backgroundColor,
              },
              contentStyle,
            ]}
          >
            {header}
            {children}
          </View>
        </View>
      );
    }

    // bicolor background
    if (radioTab?.backgroundType === 'bicolor' && radioTab.backgroundColorSecondary) {
      return (
        <View style={[styles.content, { backgroundColor: COLORS.WHITE }]}>
          <BicolorBackground
            colorBottom={radioTab.backgroundColorSecondary}
            colorTop={radioTab.backgroundColor}
            direction={gradientBackgroundDirection}
          >
            <View style={styles.headerAndContentContainer}>
              {header}
              {children}
            </View>
          </BicolorBackground>
        </View>
      );
    }

    if (radioTab?.backgroundType === 'gradient' && radioTab?.backgroundColorSecondary) {
      return (
        <View style={[styles.content, { backgroundColor: COLORS.WHITE }]}>
          <GradientBackground
            colors={[radioTab.backgroundColor, radioTab.backgroundColorSecondary]}
            style={{ flex: 1 }}
            direction={gradientBackgroundDirection}
          >
            <View style={styles.headerAndContentContainer}>
              {header}
              {children}
            </View>
          </GradientBackground>
        </View>
      );
    }
  }

  // image background
  else if (backgroundOption === 'image' && backgroundImage) {
    return (
      <View style={[styles.content, { backgroundColor: COLORS.WHITE }]}>
        <ImageBackground
          source={backgroundImage}
          style={[styles.content, { backgroundColor: COLORS.WHITE }, contentStyle]}
          blurRadius={showBlurredBackground ? DEFAULT_BACKGROUND_BLUR_RADIUS : 0}
        >
          {header}
          {children}
        </ImageBackground>
      </View>
    );
  }

  // cover background
  else if (backgroundOption === 'cover') {
    return (
      <View style={[styles.content, { backgroundColor: COLORS.WHITE }]}>
        <ImageBackground
          source={
            currentTrack.image ? { uri: currentTrack.image } : defaultBackgroundCover
          }
          style={[styles.content, { backgroundColor: COLORS.WHITE }, contentStyle]}
          blurRadius={showBlurredBackground ? DEFAULT_BACKGROUND_BLUR_RADIUS : 0}
        >
          {header}
          {children}
        </ImageBackground>
      </View>
    );
  }

  // fallback to default
  return (
    <View style={[styles.content, { backgroundColor: COLORS.WHITE }, contentStyle]}>
      {header}
      {children}
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    backgroundColor: IS_WEB ? '#00000070' : undefined, // remove background color flare
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    height: mvs(69),
  },
  headerAndContentContainer: {
    flex: 1,
    justifyContent: 'space-between',
  },
  webOffset: {
    paddingTop: 25,
  },
});
